import { createSelector } from "reselect"

import { Symbols } from "@tenforce/toolbox-fontmap"
import { doesStringContainHTML } from "../../util/Strings"
import { stripTags } from "../../util/Strings"

import { OwnProps as DetailPageProps } from "./components/DetailPageInlineAttachment"
import { OwnProps as CardViewProps } from "./components/CardViewInlineAttachment"
import { ICardView } from "../cardview"
import { IAttachment, IInlineAttachments, ILinkedDocument } from "./index"

export const transformLinkedDocument = (
    document: ILinkedDocument,
): IAttachment => {
    const {
        Creator,
        LinkReason,
        LinkedDocumentId,
        OriginalFileName,
        CreationText,
        FileSize,
        UrlString,
        AttachmentType,
        IsImage,
    } = document

    return {
        creator: stripTags(Creator),
        description: LinkReason,
        isDescriptionHtml: doesStringContainHTML(LinkReason),
        key: LinkedDocumentId,
        fileName: OriginalFileName,
        fileProperties:
            stripTags(CreationText).replace(" at ", " ") +
            ` ${Symbols.Dot} ` +
            FileSize,
        url: {
            F: "~/download/attachment/" + LinkedDocumentId,
            U: UrlString,
        }[AttachmentType],
        thumbnail: IsImage
            ? "~/download/attachmentthumb/" + LinkedDocumentId
            : undefined,
    }
}

// INLINE ATTACHMENT IN CUSTOM FIELD

const getCustomFieldLinkedDocument = (
    state: IInlineAttachments,
    props: DetailPageProps,
): ILinkedDocument[] =>
    state.attachmentFields[props.customFieldDefinitionId].linkedDocuments

const getCustomFieldShowAllDocuments = (
    state: IInlineAttachments,
    props: DetailPageProps,
): boolean =>
    state.attachmentFields[props.customFieldDefinitionId].showAllDocuments

const getCustomFieldAttachmentLoadCounter = (
    state: IInlineAttachments,
    props: DetailPageProps,
): number =>
    state.attachmentFields[props.customFieldDefinitionId]
        .attachmentsToLoadCounter

export interface AttachmentField {
    attachments: IAttachment[]
    showAllDocuments: boolean
    attachmentLoadCounter: number
}

export const getCustomFieldAttachments = createSelector(
    [getCustomFieldLinkedDocument],
    (linkedDocuments): IAttachment[] =>
        linkedDocuments.map(transformLinkedDocument),
)

export const getDetailPageAttachmentField = createSelector(
    [
        getCustomFieldShowAllDocuments,
        getCustomFieldAttachments,
        getCustomFieldAttachmentLoadCounter,
    ],
    (
        showAllDocuments,
        attachments,
        attachmentLoadCounter,
    ): AttachmentField => ({
        attachments,
        showAllDocuments,
        attachmentLoadCounter,
    }),
)

// ATTACHMENTS IN CARD VIEW

const getLinkedDocumentsFromItem = (
    state: ICardView,
    props: CardViewProps,
): ILinkedDocument[] => {
    const item = state.items.find((i) => i.id === props.item.id)
    return item?.linkedDocuments ?? []
}

export const getItemCardAttachments = createSelector(
    [getLinkedDocumentsFromItem],
    (linkedDocuments): IAttachment[] =>
        linkedDocuments.map(transformLinkedDocument),
)
